import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { SessionService } from '../session.service';
import { TripService } from '../trip.service';
import {
  isGuestAuthRoute,
  isTripIdWritableRoute,
  readTripIdFromUrl,
  TRIP_ID_QUERY_PARAM,
} from './selected-trip-url.helpers';

/**
 * Keeps `tripId` in the URL in sync with `TripService` (see ADR 0004).
 *
 * URL **reads** use `selected-trip-url.helpers` (manual path/query split + `URLSearchParams`).
 * URL **writes** use `Router.navigate` with `queryParams` — do not hand-build query strings.
 *
 * We intentionally avoid `URL` / `Router.parseUrl` for reads today: Pelican routes are flat
 * (`/forms`, `/dashboard`, …) and helpers stay pure for unit tests. If routes gain matrix
 * params or encoding edge cases bite, switch helpers to `new URL(url, syntheticBase)` or
 * `router.parseUrl()` here instead of growing the split logic.
 */
@Injectable({ providedIn: 'root' })
export class SelectedTripUrlService {
  private readonly router = inject(Router);
  private readonly tripService = inject(TripService);
  private readonly sessionService = inject(SessionService);

  private readonly currentUrl = signal(this.router.url);
  private suppressUrlRead = false;

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.onNavigationEnd(event.urlAfterRedirects);
      });

    effect(() => {
      const tripId = this.tripService.currentTripId();
      const tripsLoaded = this.tripService.userTripsLoaded();
      const sessionReady = this.sessionService.sessionReady();
      const activeUserId = this.sessionService.activeUserId();
      const url = this.currentUrl();

      if (!sessionReady || activeUserId === null || !tripsLoaded) {
        return;
      }

      // Don't sync the tripId while a navigation is already in flight. On reload the
      // deep-link navigation (e.g. `/trip-finances?tripId=…`) hasn't committed yet, so the
      // router's current URL — the base `router.navigate([])` writes against — is still `/`.
      // Writing here would target `/?tripId=…`, cancel the real navigation, and bounce the
      // user to the dashboard. Once the navigation commits, `currentUrl` changes and this
      // effect re-runs to reconcile the tripId if needed. (ADR 0004)
      //
      // Read via `untracked` so this stays a point-in-time check: we don't want the effect
      // subscribing to `currentNavigation` and re-running on every navigation start/end —
      // `currentUrl` (set on NavigationEnd) is already the trigger that resumes the sync.
      if (untracked(this.router.currentNavigation) !== null) {
        return;
      }

      // Before the first navigation, router.url is still `/` even when the address bar
      // is `/onboarding`. Writing tripId here targets `/?tripId=…`, which redirects to
      // the dashboard and cancels that URL. Wait until the initial navigation commits;
      // NavigationEnd updates `currentUrl` and re-runs this effect.
      if (!this.router.navigated) {
        return;
      }

      if (!isTripIdWritableRoute(url)) {
        return;
      }

      this.writeTripIdToUrl(url, tripId);
    });
  }

  private onNavigationEnd(url: string): void {
    if (isGuestAuthRoute(url)) {
      this.tripService.setUrlTripCandidate(null);
      this.stripTripIdFromUrl(url);
      return;
    }

    if (this.suppressUrlRead) {
      this.suppressUrlRead = false;
      return;
    }

    if (!isTripIdWritableRoute(url)) {
      return;
    }

    this.tripService.setUrlTripCandidate(readTripIdFromUrl(url));
  }

  private writeTripIdToUrl(url: string, tripId: number | null): void {
    const urlTripId = readTripIdFromUrl(url);

    if (tripId === null) {
      if (urlTripId !== null) {
        this.navigateTripId(null);
      }
      return;
    }

    if (urlTripId === tripId) {
      return;
    }

    this.navigateTripId(tripId);
  }

  private stripTripIdFromUrl(url: string): void {
    if (readTripIdFromUrl(url) === null) {
      return;
    }

    this.navigateTripId(null);
  }

  private navigateTripId(tripId: number | null): void {
    // Don't push a tripId onto a guest route during bootstrap races. Clearing
    // (tripId === null) must still run so guest URLs can strip a stale param.
    if (tripId !== null && !isTripIdWritableRoute(this.router.url)) {
      return;
    }

    this.suppressUrlRead = true;
    void this.router.navigate([], {
      queryParams: { [TRIP_ID_QUERY_PARAM]: tripId },
      queryParamsHandling: 'merge',
      preserveFragment: true,
      replaceUrl: true,
    });
  }
}
